import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Plus
} from "lucide-react";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import TransactionDialog from "@/components/TransactionDialog";
import TransactionList from "@/components/TransactionList";
import StatsCards from "@/components/StatsCards";
import ChartsSection from "@/components/ChartsSection";
import AIInsights from "@/components/AIInsights";
import AICoach from "@/components/AICoach";
import BudgetGoals from "@/components/BudgetGoals";
import DividendsYTDWidget from "@/components/DividendsYTDWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [budgetRefresh, setBudgetRefresh] = useState(0);

  const fetchUser = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Not authenticated');
      const userData = await response.json();
      setUser(userData);
    } catch {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API}/categories`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('fetchCategories failed:', err);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await fetch(`${API}/transactions?currency=${currency}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (err) {
      console.error('fetchTransactions failed:', err);
    }
  }, [currency]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/transactions/stats/${currency}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('fetchStats failed:', err);
    }
  }, [currency]);

  useEffect(() => {
    fetchUser();
    fetchCategories();
  }, [fetchUser, fetchCategories]);

  useEffect(() => {
    if (user) {
      fetchTransactions();
      fetchStats();
    }
  }, [user, currency, fetchTransactions, fetchStats]);

  const handleAddTransaction = () => {
    setEditingTransaction(null);
    setIsDialogOpen(true);
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setIsDialogOpen(true);
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      const response = await fetch(`${API}/transactions/${transactionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Transaction deleted successfully');
        fetchTransactions();
        fetchStats();
        setBudgetRefresh((n) => n + 1);
      } else {
        toast.error('Failed to delete transaction');
      }
    } catch {
      toast.error('Failed to delete transaction');
    }
  };

  const handleTransactionSaved = () => {
    fetchTransactions();
    fetchStats();
    setBudgetRefresh((n) => n + 1);
    setIsDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} currency={currency} onCurrencyChange={setCurrency} onUserUpdate={setUser} />

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-bold">Dashboard</h2>
            <p className="text-sm text-muted-foreground">
              Overview of your financial health
            </p>
          </div>
          <Button
            data-testid="add-transaction-button"
            onClick={handleAddTransaction}
            className="transition-transform duration-200 hover:-translate-y-px"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Transaction
          </Button>
        </div>

        {/* Stats Cards */}
        <StatsCards stats={stats} currency={currency} />

        {/* Dividends YTD Widget */}
        <DividendsYTDWidget currency={currency} />

        {/* Budget Goals with Smart Alerts */}
        <BudgetGoals
          currency={currency}
          categories={categories}
          refreshTrigger={budgetRefresh}
        />

        {/* AI Insights */}
        <AIInsights currency={currency} />

        {/* AI Coach (multi-turn chat) */}
        <AICoach currency={currency} />

        {/* Charts Section */}
        <ChartsSection stats={stats} currency={currency} />

        {/* Transactions List */}
        <TransactionList
          transactions={transactions}
          categories={categories}
          onEdit={handleEditTransaction}
          onDelete={handleDeleteTransaction}
          currency={currency}
        />
      </main>

      {/* Transaction Dialog */}
      <TransactionDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        transaction={editingTransaction}
        categories={categories}
        currency={currency}
        onSaved={handleTransactionSaved}
      />
    </div>
  );
}

export default Dashboard;
